import { ApiResponse } from "../models/api-response";
import { callApi } from "./api.service";
import {
  PlanFromAPI,
  PlansResponse,
  UserPlanSummary,
  AssignFixedPlanRequest,
  AssignPaygPlanRequest,
  UpdatePlanLimitsRequest,
  FixedPlanDefinitions,
  UserListItem,
} from '../types/plan.types';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

// Re-export types for external use
export type { UserPlanSummary, AssignFixedPlanRequest, AssignPaygPlanRequest, UserListItem };

// --- Service Functions ---

/**
 * Get all users for admin management.
 * @param accessToken - The admin's access token.
 * @returns Promise resolving to an ApiResponse containing the list of users.
 */
export const getAllUsers = async (
  accessToken: string
): Promise<ApiResponse<UserListItem[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<UserListItem[]>(`${apiServerUrl}/api/usage/users`, config);
};

/**
 * Get user's current plan and usage summary.
 * @param accessToken - The admin's access token.
 * @param userId - The user ID to get plan summary for.
 * @returns Promise resolving to an ApiResponse containing the user's plan summary.
 */
export const getUserPlanSummary = async (
  accessToken: string,
  userId: string
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<UserPlanSummary>(`${apiServerUrl}/api/usage/users/${userId}/plan`, config);
};

/**
 * Assign a fixed plan to a user.
 * @param accessToken - The admin's access token.
 * @param userId - The user ID to assign the plan to.
 * @param request - The fixed plan assignment request.
 * @returns Promise resolving to an ApiResponse containing the updated plan summary.
 */
export const assignFixedPlan = async (
  accessToken: string,
  userId: string,
  request: AssignFixedPlanRequest
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  };

  return callApi<UserPlanSummary>(
    `${apiServerUrl}/api/usage/users/${userId}/assign-fixed-plan`,
    config
  );
};

/**
 * Assign a pay-as-you-go plan to a user.
 * @param accessToken - The admin's access token.
 * @param userId - The user ID to assign the plan to.
 * @param request - The PAYG plan assignment request.
 * @returns Promise resolving to an ApiResponse containing the updated plan summary.
 */
export const assignPaygPlan = async (
  accessToken: string,
  userId: string,
  request: AssignPaygPlanRequest
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  };

  return callApi<UserPlanSummary>(
    `${apiServerUrl}/api/usage/users/${userId}/assign-payg-plan`,
    config
  );
};

/**
 * Update limits for a user's PAYG plan.
 * @param accessToken - The admin's access token.
 * @param userId - The user ID to update limits for.
 * @param request - The plan limits update request.
 * @returns Promise resolving to an ApiResponse containing the updated plan summary.
 */
export const updatePlanLimits = async (
  accessToken: string,
  userId: string,
  request: UpdatePlanLimitsRequest
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  };

  return callApi<UserPlanSummary>(
    `${apiServerUrl}/api/usage/users/${userId}/update-limits`,
    config
  );
};

/**
 * Update only the expiration date on a user's tenant plan.
 */
export const updateExpiration = async (
  accessToken: string,
  userId: string,
  expiresAt: string
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ expires_at: expiresAt }),
  };

  return callApi<UserPlanSummary>(
    `${apiServerUrl}/api/usage/users/${userId}/update-expiration`,
    config
  );
};

/**
 * Get available fixed plan definitions.
 * @param accessToken - The admin's access token.
 * @returns Promise resolving to an ApiResponse containing the fixed plan definitions.
 */
export const getFixedPlanDefinitions = async (
  accessToken: string
): Promise<ApiResponse<FixedPlanDefinitions>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<FixedPlanDefinitions>(`${apiServerUrl}/api/usage/fixed-plans`, config);
};

/**
 * Delete a user's current plan.
 * @param accessToken - The admin's access token.
 * @param userId - The user ID to delete the plan for.
 * @returns Promise resolving to an ApiResponse containing the deletion result.
 */
export const deleteUserPlan = async (
  accessToken: string,
  userId: string
): Promise<ApiResponse<{ message: string }>> => {
  const config: RequestInit = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<{ message: string }>(`${apiServerUrl}/api/usage/users/${userId}/plan`, config);
};

/**
 * Get the current user's own plan and usage summary.
 * @param accessToken - The user's access token.
 * @returns Promise resolving to an ApiResponse containing the user's plan summary.
 */
export const getMyPlanSummary = async (
  accessToken: string
): Promise<ApiResponse<UserPlanSummary>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<UserPlanSummary>(`${apiServerUrl}/api/usage/my-plan`, config);
};

/**
 * Get all available plans from database (new structure).
 * @param accessToken - The admin's access token.
 * @returns Promise resolving to an ApiResponse containing all plans.
 */
// Re-export types for backward compatibility
export type PlanFromDB = PlanFromAPI;

export const getAllPlans = async (
  accessToken: string
): Promise<ApiResponse<PlansResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<PlansResponse>(`${apiServerUrl}/api/usage/plans`, config);
};